import { ConsentCondition, ConsentConditionType, ConsentConditionWithId } from '@/api';
import { ConsentConditionsApi } from '@/api';
import { isArray } from '@amcharts/amcharts4/core';
import { isConsentConditionType } from '@/modules';

type Override<T1, T2> = Omit<T1, keyof T2> & T2;
export type ConsentConditionWithParams = Override<
	ConsentCondition,
	{
		consentconditiontype: ConsentConditionType;
		parameters:
			| VolumeConsentParameters
			| RollingPeriodVolumeParameters
			| FixedPeriodVolumeParameters
			| FixedDateVolumeParameters;
	}
>;

export function isConsentConditionWithParams(x: any): x is ConsentConditionWithParams {
	if (typeof x !== 'object') return false;
	if (typeof x.consentconditiontype !== 'object') return false;
	if (!isConsentConditionType(x.consentconditiontype)) return false;

	if (
		x.consentconditiontype.name === 'volumePerSecond' &&
		isVolumeConsentParameters(x.parameters)
	) {
		return true;
	}

	if (
		x.consentconditiontype.name === 'rollingPeriodVolume' &&
		isRollingPeriodVolumeParameters(x.parameters)
	) {
		return true;
	}

	if (
		x.consentconditiontype.name === 'fixedPeriodVolume' &&
		isFixedPeriodVolumeParameters(x.parameters)
	) {
		return true;
	}

	if (
		x.consentconditiontype.name === 'fixedDateVolume' &&
		isFixedDateVolumeParameters(x.parameters)
	) {
		return true;
	}
	return false;
}

export const VolumeUnitTypes = ['litres', 'cubicMetres'];
export type VolumeConsentParameters = {
	volumeValue: number;
	volumeUnit: typeof VolumeUnitTypes[number];
	waterMeterIds: number[];
};

export function isVolumeConsentParameters(x: any): x is VolumeConsentParameters {
	if (typeof x !== 'object') return false;
	if (isNaN(x.volumeValue)) return false;
	if (isArray(x.waterMeterIds) === false) return false;
	for (const id of x.waterMeterIds) {
		if (typeof id !== 'number') return false;
	}
	console.log(x.volumeUnit);
	return typeof x.volumeUnit === 'string' && VolumeUnitTypes.indexOf(x.volumeUnit) !== -1;
}

export const PeriodUnitTypes = ['hour', 'day', 'week', 'month'];
export type RollingPeriodVolumeParameters = VolumeConsentParameters & {
	periodValue: number;
	periodUnit: typeof PeriodUnitTypes[number];
};

export function isRollingPeriodVolumeParameters(x: any): x is RollingPeriodVolumeParameters {
	if (typeof x !== 'object') return false;
	if (isNaN(x.periodValue)) return false;
	if (typeof x.periodUnit !== 'string' || PeriodUnitTypes.indexOf(x.periodUnit) === -1) {
		return false;
	}
	return isVolumeConsentParameters(x);
}

export type FixedPeriodVolumeParameters = RollingPeriodVolumeParameters & {
	startDate?: string;
};

export function isFixedPeriodVolumeParameters(x: any) {
	return isRollingPeriodVolumeParameters(x);
}

export type FixedDateVolumeParameters = VolumeConsentParameters & {
	startDay: number;
	startMonth: number;
	endDay: number;
	endMonth: number;
};

export function isFixedDateVolumeParameters(x: any): x is FixedDateVolumeParameters {
	if (typeof x !== 'object') return false;
	if (typeof x.startDay !== 'number' || x.startDay < 1 || x.startDay > 31) return false;
	if (typeof x.startMonth !== 'number' || x.startMonth < 1 || x.startMonth > 12) return false;
	if (typeof x.endDay !== 'number' || x.endDay < 1 || x.endDay > 31) return false;
	if (typeof x.endMonth !== 'number' || x.endMonth < 1 || x.endMonth > 12) return false;
	return isVolumeConsentParameters(x);
}

export class ConsentConditionModule {
	private api: ConsentConditionsApi;
	constructor() {
		this.api = new ConsentConditionsApi();
	}

	async get(conditionId: number): Promise<ConsentConditionWithParams | undefined> {
		const res = await this.api.getConsentConditionById(conditionId, {
			params: { include: 'consentConditionType' },
		});

		if (res != undefined && res.data != undefined) {
			if (isConsentConditionWithParams(res.data)) {
				return res.data;
			} else {
				throw Error(
					`Invalid ConsentCondition  with params from get from API. \n ${JSON.stringify(
						res.data,
					)} `,
				);
			}
		}

		return undefined;
	}

	async save(
		consentCondition: ConsentConditionWithParams,
		consentId: number | undefined,
	): Promise<ConsentConditionWithId> {
		if (!isConsentConditionWithParams(consentCondition)) {
			throw Error(
				`Invalid ConsentCondition with params for save via API. \n ${JSON.stringify(
					consentCondition,
				)} `,
			);
		}

		if (consentId != undefined) {
			return (
				await this.api.patchConsentCondition(consentId, <ConsentConditionWithId>consentCondition)
			).data;
		} else {
			return (await this.api.postConsentCondition(<ConsentCondition>consentCondition)).data;
		}
	}

	async delete(consentConditionId: number): Promise<void> {
		await this.api.deleteConsentCondtion(consentConditionId);
	}
}
